import { SelectHTMLAttributes, forwardRef } from "react";
import { getCompactInputClasses } from "./inputStyles";

export interface CompactSelectOption {
	/** When true, the option is rendered disabled and cannot be selected. */
	disabled?: boolean;
	label: string;
	/** Native `title` attribute — shown as a hover tooltip on the option. */
	title?: string;
	value: string | number;
}

export interface CompactSelectProps
	extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "children"> {
	/** Show error styling */
	hasError?: boolean;
	/** Options rendered as <option> elements. */
	options: CompactSelectOption[];
	/** Optional placeholder shown as a disabled-value first option. */
	placeholder?: string;
	/** Additional CSS classes on the <select> element */
	selectClassName?: string;
}

/**
 * Compact native <select> that shares the same border, focus, and colour
 * styles as CompactTextInput. Use in inline forms, spreadsheet cells, and
 * other tight layouts where a full react-select dropdown is too heavy.
 *
 * Native <select> is intentional: it keeps the bundle small, plays nicely
 * with keyboard / a11y on mobile, and matches the rest of the compact
 * input family.
 */
export const CompactSelect = forwardRef<
	HTMLSelectElement,
	CompactSelectProps
>(
	(
		{
			hasError = false,
			options,
			placeholder,
			selectClassName = "",
			className = "",
			value,
			...props
		},
		ref
	) => {
		// Slightly less horizontal padding than the text input so the native
		// dropdown arrow sits at a sensible distance from the value.
		return (
			<select
				ref={ref}
				value={value ?? ""}
				className={getCompactInputClasses(
					hasError,
					`text-sm pr-8 ${selectClassName} ${className}`
				)}
				{...props}
			>
				{placeholder !== undefined && (
					<option value="">{placeholder}</option>
				)}
				{options.map((opt) => (
					<option
						key={opt.value}
						value={opt.value}
						disabled={opt.disabled}
						title={opt.title}
					>
						{opt.label}
					</option>
				))}
			</select>
		);
	}
);

CompactSelect.displayName = "CompactSelect";
